import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { pricingPathFor } from './utils/featurePlans';
import { AuthProvider } from './context/AuthContext';
import DashboardLayout from './layouts/DashboardLayout';
import ProGate from './components/ProGate';
import Home from './pages/Home';
import Landing from './pages/Landing';
import { ThemeGate } from './theme/ThemeGate';
import './styles/globals.css';
import './print/print.css';

// Back-compat redirect: the old /trading/:asset URL now lives at /risk-analytics/:asset (neutral
// analytics vocabulary). Preserves the asset param so existing tabs/bookmarks keep working.
function TradingRedirect() {
  const { asset } = useParams();
  return <Navigate to={`/risk-analytics/${asset ?? ''}`} replace />;
}


const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const AgentGovernancePage = lazy(() => import('./pages/AgentGovernancePage'));
const AssistantPage = lazy(() => import('./pages/AssistantPage'));
const AlertSubscriptionsPage = lazy(() => import('./pages/AlertSubscriptionsPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const ForwardEvidencePage = React.lazy(() => import('./pages/ForwardEvidencePage'));
const PortfolioEntanglementPage = lazy(() => import('./pages/PortfolioEntanglementPage'));
// Institutional Workspace (phase 0, hidden): the page gates itself on the institutional_workspace entitlement and
// renders nothing for other callers (no upsell — unreleased modules are not advertised).
const InstitutionalWorkspacePage = lazy(() => import('./pages/InstitutionalWorkspacePage'));
const OperationalDashboardPage = lazy(() => import('./pages/OperationalDashboardPage'));
const RiskKernelPage = lazy(() => import('./pages/RiskKernelPage'));
const TradingPage = lazy(() => import('./pages/TradingPage'));
const EcosystemPage = lazy(() => import('./pages/EcosystemPage'));
const EcosystemV2Page = lazy(() => import('./pages/EcosystemV2Page'));
const DetailedAnalysisPage = lazy(() => import('./pages/DetailedAnalysisPage'));
// ASSET BRIEF (phase 1, admin-only — the page renders a plain 404 for every other caller).
const AssetBriefPage = lazy(() => import('./pages/AssetBriefPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
// Identity & Access stage B pages (one module).
const VerifyEmailPage = lazy(() => import('./pages/IdentityPages').then((m) => ({ default: m.VerifyEmailPage })));
const ForgotPasswordPage = lazy(() => import('./pages/IdentityPages').then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('./pages/IdentityPages').then((m) => ({ default: m.ResetPasswordPage })));
const ConfirmEmailChangePage = lazy(() => import('./pages/IdentityPages').then((m) => ({ default: m.ConfirmEmailChangePage })));
const InvitePage = lazy(() => import('./pages/IdentityPages').then((m) => ({ default: m.InvitePage })));
const SecurityPage = lazy(() => import('./pages/IdentityPages').then((m) => ({ default: m.SecurityPage })));

const PageFallback = () => (
  <div className="flex items-center justify-center h-screen bg-cmd-bg">
    <div className="text-cmd-textDim text-xs uppercase tracking-[0.14em] animate-pulse">
      Loading…
    </div>
  </div>
);

// The old /pro?feature= upsell route now lands on the plans page with the same feature context (owner 2026-10-04).
function LegacyProRedirect() {
  const [params] = useSearchParams();
  return <Navigate to={pricingPathFor(params.get('feature') || undefined)} replace />;
}

function App() {
  // No client-side role backdoor: role/tier/entitlements come from the server via AuthContext
  // (/api/auth/me). Granting admin on localhost would let a localStorage edit imply access the
  // backend never granted — the paywall is enforced server-side now.
  const basename =
    typeof window !== 'undefined' && window.location.pathname.startsWith('/saa-dashboard')
      ? '/saa-dashboard'
      : '/';
  return (
    <AuthProvider>
    <Router
      basename={basename}
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <ThemeGate />
      <div className="App min-h-screen bg-cmd-bg text-cmd-text">
        <Suspense fallback={<PageFallback />}>
          <Routes>
            {/* Public pages */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/confirm-email-change" element={<ConfirmEmailChangePage />} />
            <Route path="/invite" element={<InvitePage />} />
            <Route path="/account/security" element={<SecurityPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/account" element={<DashboardLayout><AccountPage /></DashboardLayout>} />
            {/* Entry landing ("превход") — the unauthenticated front door. Terminal lives at /app. */}
            <Route path="/" element={<Landing />} />

            {/* Dashboard pages — the analysis terminal now lives at /app (was /). */}
            <Route path="/app" element={<DashboardLayout><Home /></DashboardLayout>} />
            <Route path="/brief" element={<DashboardLayout><AssetBriefPage /></DashboardLayout>} />
            <Route path="/brief/:asset" element={<DashboardLayout><AssetBriefPage /></DashboardLayout>} />
            <Route path="/dashboard" element={<Navigate to="/app" replace />} />
            {/* Free-tier upsell landing (shown by locked controls). */}
            <Route path="/pro" element={<LegacyProRedirect />} />
            <Route path="/reports" element={<DashboardLayout><ProGate feature="reports"><ReportsPage /></ProGate></DashboardLayout>} />
            <Route path="/operational" element={<DashboardLayout><OperationalDashboardPage /></DashboardLayout>} />
            {/* v1A: Scenarios merged into Portfolio. Redirect the old route to the anchor. */}
            <Route path="/scenario" element={<Navigate to="/portfolio#scenarios" replace />} />
            <Route path="/scenarios" element={<Navigate to="/portfolio#scenarios" replace />} />
            {/* Risk Analytics (formerly "Trading Intelligence") — standalone full page, opened in a new tab (Pro).
                Renamed to neutral analytics vocabulary; the old /trading URLs 301-redirect here so existing tabs work. */}
            <Route path="/risk-analytics" element={<ProGate feature="scenario"><TradingPage /></ProGate>} />
            <Route path="/risk-analytics/:asset" element={<ProGate feature="scenario"><TradingPage /></ProGate>} />
            <Route path="/trading" element={<Navigate to="/risk-analytics" replace />} />
            <Route path="/trading/:asset" element={<TradingRedirect />} />
            {/* Ecosystem Impact graph — standalone full page, opened in a new tab (Pro) */}
            <Route path="/ecosystem" element={<ProGate feature="ecosystem"><EcosystemPage /></ProGate>} />
            <Route path="/ecosystem/:asset" element={<ProGate feature="ecosystem"><EcosystemPage /></ProGate>} />
            {/* Risk Transmission Map (Ecosystem v2) — React Flow + ELK, typed topology + per-edge co-movement + single-hop β scenario */}
            {/* Detailed Analysis (Dossier View) — standalone page in its own window, canonical header/footer. */}
            <Route path="/analysis/:asset" element={<ProGate feature="detailed-analysis"><DetailedAnalysisPage /></ProGate>} />
            <Route path="/ecosystem/v2/:asset" element={<ProGate feature="ecosystem"><EcosystemV2Page /></ProGate>} />
            <Route path="/risk-kernel" element={<DashboardLayout><ProGate feature="risk-kernel"><RiskKernelPage /></ProGate></DashboardLayout>} />            <Route path="/agent" element={<DashboardLayout><ProGate feature="agent"><AgentGovernancePage /></ProGate></DashboardLayout>} />
            <Route path="/assistant" element={<DashboardLayout><AssistantPage /></DashboardLayout>} />
            <Route path="/alerts" element={<DashboardLayout><ProGate feature="alerts"><AlertSubscriptionsPage /></ProGate></DashboardLayout>} />
            <Route path="/portfolio" element={<DashboardLayout><ProGate feature="portfolio"><PortfolioEntanglementPage /></ProGate></DashboardLayout>} />
            <Route path="/institutional" element={<DashboardLayout><InstitutionalWorkspacePage /></DashboardLayout>} />
            <Route path="/institutional/:group" element={<DashboardLayout><InstitutionalWorkspacePage /></DashboardLayout>} />
            <Route path="/admin" element={<DashboardLayout><AdminPage /></DashboardLayout>} />
            <Route path="/forward-evidence" element={<DashboardLayout><ForwardEvidencePage /></DashboardLayout>} />
          </Routes>
        </Suspense>
      </div>
    </Router>
    </AuthProvider>
  );
}

export default App;
